import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import { IndexPath } from '@/Config/app/router/index.path';
import type { AIAssistantsData } from '@/Domain/components/TopBar/types';

// Pages that build/configure the AI agent. Editor and above can reach them;
// support/viewer (and unrolled users) get bounced to AI Preview.
const AI_PERMISSION_PAGES = [
  IndexPath.identity,
  IndexPath.knowledgeBase.root,
  IndexPath.website,
  IndexPath.chatPage,
  IndexPath.channels,
];

// `IndexPath.settings` is intentionally NOT gated here. The SettingsPage
// handles its own per-tab gating: Account is always accessible (user's own
// profile/preferences) while Organization and Billing are role-disabled with
// a tooltip. Bouncing the entire URL would block support/viewer users from
// the Account tab, which they should be able to reach.
//
// Legacy paths /subscription, /account-settings, and /user/profile redirect
// to /settings via <RedirectToSettings>. They are not gated either so the
// redirect can land users on the right tab.

export const usePermissions = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const aiAssistants = useQuery<AIAssistantsData>({ queryKey: [QueryKey.aiAssistants.get] });

  const permissions = useMemo(() => {
    if (aiAssistants?.data?.current_ai_assistant?.permissions) {
      return {
        hasAIAssistantAccess: aiAssistants.data.current_ai_assistant.permissions.ai_assistant,
        hasConversationAccess: aiAssistants.data.current_ai_assistant.permissions.conversations,
        hasAccountSettingsAccess:
          aiAssistants.data.current_ai_assistant.permissions.account_settings,
        hasSubscriptionAccess: aiAssistants.data.current_ai_assistant.permissions.subscription,
      };
    }

    return {
      hasAIAssistantAccess: true,
      hasConversationAccess: true,
      hasAccountSettingsAccess: true,
      hasSubscriptionAccess: true,
    };
  }, [aiAssistants?.data?.current_ai_assistant?.permissions]);

  const hasMatch = (paths: string[]) => paths.find((path) => location.pathname.includes(path));

  useEffect(() => {
    if (hasMatch(AI_PERMISSION_PAGES) && !permissions.hasAIAssistantAccess) {
      navigate(IndexPath.aiPreview, { viewTransition: true });
      return;
    }
  }, [location.pathname, aiAssistants?.data]);

  return null;
};
